if (!customElements.get('faq-hub')) {
  customElements.define('faq-hub', class extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.searchInput = this.querySelector('[data-faq-search]');
      this.controls = this.querySelector('[data-faq-controls]');
      this.status = this.querySelector('[data-faq-status]');
      this.emptyState = this.querySelector('[data-faq-empty]');
      this.clearButton = this.querySelector('[data-faq-clear]');
      this.rail = this.querySelector('[data-faq-rail]');
      this.previousButton = this.querySelector('[data-faq-previous]');
      this.nextButton = this.querySelector('[data-faq-next]');
      this.groups = Array.from(this.querySelectorAll('[data-faq-group]'))
        .filter((element) => element.closest('faq-hub') === this)
        .map((element) => ({
          element,
          id: element.dataset.faqGroup,
          questions: Array.from(element.querySelectorAll('[data-faq-question]'))
            .filter((question) => question.closest('[data-faq-group]') === element)
            .map((question) => ({
              element: question,
              text: this.normalize([
                question.querySelector('summary')?.textContent || '',
                question.querySelector('.faq-hub__answer')?.textContent || '',
              ].join(' ')),
            })),
        }));
      this.categories = Array.from(this.rail?.querySelectorAll('[data-faq-category]') || [])
        .map((link) => ({
          link,
          group: this.groups.find((group) => group.id === link.dataset.faqCategory),
        }))
        .filter(({ group }) => group?.questions.length);

      if (!this.searchInput || !this.controls || !this.status || !this.categories.length) return;

      try {
        const messages = JSON.parse(this.querySelector('[data-faq-messages]')?.textContent || '{}');
        this.messages = messages.counts || messages;
      } catch {
        this.messages = {};
      }

      this.activeCategory = this.categories.some(({ group }) => group.id === this.activeCategory)
        ? this.activeCategory
        : this.categories[0].group.id;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.searchInput.addEventListener('input', () => this.scheduleSearch(), { signal });
      this.searchInput.addEventListener('search', () => this.applySearch(), { signal });
      this.searchInput.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.searchInput.value) return;
        event.preventDefault();
        this.clearSearch();
      }, { signal });
      this.clearButton?.addEventListener('click', () => this.clearSearch(), { signal });
      this.querySelector('[data-faq-reset]')?.addEventListener('click', () => this.clearSearch(), { signal });
      this.categories.forEach(({ link, group }) => {
        link.addEventListener('click', (event) => {
          if (event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
          event.preventDefault();
          this.selectCategory(group.id);
          link.focus({ preventScroll: true });
          this.revealRailLink(link);
        }, { signal });
      });
      this.rail.addEventListener('keydown', (event) => this.onCategoryKeydown(event), { signal });
      this.rail.addEventListener('scroll', () => this.scheduleRailUpdate(), { passive: true, signal });
      this.previousButton?.addEventListener('click', () => this.moveRail(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.moveRail(1), { signal });
      window.addEventListener('resize', () => this.scheduleRailUpdate(), { passive: true, signal });
      window.addEventListener('hashchange', () => this.revealHash(), { signal });
      document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });
      document.addEventListener('click', (event) => this.onHashLinkClick(event), { signal });

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleRailUpdate());
        this.resizeObserver.observe(this.rail);
        this.categories.forEach(({ link }) => this.resizeObserver.observe(link));
      }

      this.classList.add('is-enhanced');
      this.controls.hidden = false;
      this.status.hidden = false;
      this.applySearch();
      this.revealHash();
      this.scheduleRailUpdate();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      clearTimeout(this.searchTimer);
      cancelAnimationFrame(this.railFrame);
      cancelAnimationFrame(this.hashFrame);
      this.searchTimer = null;
      this.railFrame = null;
      this.hashFrame = null;
      this.classList.remove('is-enhanced');
      if (this.controls) this.controls.hidden = true;
      if (this.status) this.status.hidden = true;
      if (this.emptyState) this.emptyState.hidden = true;
      this.groups?.forEach(({ element, questions }) => {
        element.hidden = false;
        questions.forEach((question) => { question.element.hidden = false; });
      });
      this.categories?.forEach(({ link }) => link.removeAttribute('aria-current'));
    }

    normalize(value) {
      return value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase().replace(/œ/g, 'oe').replace(/æ/g, 'ae').trim();
    }

    get motionBehavior() {
      return this.motionQuery.matches ? 'auto' : 'smooth';
    }

    get railDirection() {
      return getComputedStyle(this.rail).direction === 'rtl' ? -1 : 1;
    }

    scheduleSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.applySearch(), 120);
    }

    applySearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
      cancelAnimationFrame(this.hashFrame);
      this.hashFrame = null;
      const query = this.normalize(this.searchInput.value);
      const tokens = query ? query.split(/\s+/) : [];
      let visibleCount = 0;

      this.groups.forEach(({ element, id, questions }) => {
        let groupCount = 0;
        questions.forEach((question) => {
          const visible = tokens.length
            ? tokens.every((token) => question.text.includes(token))
            : id === this.activeCategory;
          question.element.hidden = !visible;
          if (visible) groupCount += 1;
        });
        element.hidden = groupCount === 0;
        visibleCount += groupCount;
      });
      this.categories.forEach(({ link, group }) => {
        if (!tokens.length && group.id === this.activeCategory) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      const messages = this.messages[tokens.length ? 'results' : 'questions'];
      this.status.textContent = messages?.[String(visibleCount)] || String(visibleCount);
      if (this.emptyState) this.emptyState.hidden = visibleCount !== 0;
      if (this.clearButton) this.clearButton.hidden = !this.searchInput.value;
    }

    clearSearch() {
      this.searchInput.value = '';
      this.applySearch();
      this.searchInput.focus({ preventScroll: true });
    }

    selectCategory(id) {
      this.activeCategory = id;
      this.searchInput.value = '';
      this.applySearch();
    }

    onCategoryKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const index = this.categories.findIndex(({ link }) => link === event.target || link.contains(event.target));
      if (index < 0) return;
      let nextIndex;
      if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = this.categories.length - 1;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        const step = (event.key === 'ArrowLeft' ? -1 : 1) * this.railDirection;
        nextIndex = (index + step + this.categories.length) % this.categories.length;
      } else return;

      event.preventDefault();
      const { link, group } = this.categories[nextIndex];
      this.selectCategory(group.id);
      link.focus({ preventScroll: true });
      this.revealRailLink(link);
    }

    revealRailLink(link) {
      const railBounds = this.rail.getBoundingClientRect();
      const linkBounds = link.getBoundingClientRect();
      let distance = 0;
      if (linkBounds.left < railBounds.left) distance = linkBounds.left - railBounds.left;
      else if (linkBounds.right > railBounds.right) distance = linkBounds.right - railBounds.right;
      if (distance) this.rail.scrollBy({ left: distance, behavior: this.motionBehavior });
    }

    moveRail(direction) {
      this.rail.scrollBy({
        left: direction * this.railDirection * this.rail.clientWidth * 0.75,
        behavior: this.motionBehavior,
      });
    }

    scheduleRailUpdate() {
      if (this.railFrame != null) return;
      this.railFrame = requestAnimationFrame(() => {
        this.railFrame = null;
        const maximum = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
        const position = Math.max(0, Math.min(maximum, Math.abs(this.rail.scrollLeft)));
        const overflowing = maximum > 2;
        [[this.previousButton, position <= 2], [this.nextButton, position >= maximum - 2]]
          .forEach(([button, atEdge]) => {
            if (!button) return;
            button.hidden = !overflowing;
            button.disabled = !overflowing || atEdge;
          });
      });
    }

    findHashTarget(hash) {
      let id;
      try { id = decodeURIComponent(hash.replace(/^#/, '')); } catch { return null; }
      if (!id) return null;
      for (const group of this.groups) {
        const question = group.questions.find(({ element }) => element.id === id);
        if (question) return { group, element: question.element, question: true };
        if (group.element.id === id && group.questions.length) return { group, element: group.element };
      }
      return null;
    }

    revealHash(hash = window.location.hash) {
      const target = this.findHashTarget(hash);
      if (target) this.revealTarget(target);
    }

    revealTarget({ group, element, question }) {
      this.selectCategory(group.id);
      if (question) element.open = true;
      const category = this.categories.find((entry) => entry.group === group);
      if (category) this.revealRailLink(category.link);
      cancelAnimationFrame(this.hashFrame);
      this.hashFrame = requestAnimationFrame(() => {
        this.hashFrame = null;
        element.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: this.motionBehavior });
      });
    }

    onHashLinkClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      let url;
      try { url = new URL(link.href, window.location.href); } catch { return; }
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.search !== window.location.search) return;
      this.revealHash(url.hash);
    }

    onBlockSelect(event) {
      const blockId = event.detail?.blockId;
      if (!blockId) return;
      for (const group of this.groups) {
        const question = group.questions.find(({ element }) => element.dataset.faqBlockId === String(blockId));
        if (!question) continue;
        this.revealTarget({ group, element: question.element, question: true });
        return;
      }
    }
  });
}
